import React, { useEffect, useRef } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import Home from "@pages/home/Home";
import Header from "@components/header/Header";
import styled from "@emotion/styled";
import Footer from "@components/footer/Footer";
import {
  AccountManagement,
  ChangePassword,
  DeliveryManagement,
  Faq,
  MyPage,
  OneInquiry,
  Review,
  SalesPolicy,
  SellBuyInquiry,
  SettingApp,
  Subscribe,
  SubscribeDetail,
} from "@pages/myPage";
import { JoinPage, VerifyEmail } from "@pages/join";
import { Saved } from "@pages/saved";
import { ProductDetail, ProductQnA } from "@pages/product";
import PurchaseFooter from "@components/footer/PurchaseFooter";
import Seller from "@pages/seller/Seller";
import { Search, SearchDetail } from "@pages/search";

import { Follow } from "@pages/follow";
import { Brand } from "@pages/brand";
import FindPasswordPage from "@pages/findpassword/FindPasswordPage";
import {
  MainRegister,
  ProductPopup,
  RegisterProduct,
  SuccessRegister,
} from "@pages/register";
import ProfileEditPage from "@pages/profileEdit/ProfileEditPage";
import LoginPage from "@pages/Login/LoginPage";
import { Private, Public } from "@routes/index";
import useAuth from "@hook/useAuth";
import { useApiRequest } from "@utils/ApiRequest";
import { useQuery } from "@tanstack/react-query";
import useUserInfoStore from "@store/useUserInfoStore";
import { Cart } from "@pages/cartPage";
import Order from "@pages/order/Order";
import { BankAccountManagement } from "@pages/myPage/_component";
import PleaseLogin from "@pages/home/PleaseLogin";
import { Alarm, NewsDetail } from "@pages/alarm";
import {
  SearchBrand,
  SearchProduct,
  SearchSeller,
} from "@pages/search/_components";
import KakaoRedirect from "@pages/Login/KakaoRedirect";
import PrivacyPolicy from "@pages/privacyPolicy/PrivacyPolicy";
import Terms from "@pages/privacyPolicy/Terms";
import AppRedirect from "@pages/app/AppRedirect";
import ProductEdit from "@pages/productEdit/ProductEdit";

import OrderList from "@pages/order/OrderList";
import OrderDetail from "@pages/order/OrderDetail";
import { AddReview } from "@pages/review";
import SalesList from "@pages/sales/SalesList";
import OrderSuccess from "@pages/order/OrderSuccess";
import SalesDetail from "@pages/sales/SalesDetail";
import SubscribeSuccess from "@pages/subscribe/SubscribeSuccess";
import SubscribeFail from "@pages/subscribe/SubscribeFail";
import CategorySearchDetail from "@pages/search/CategorySearchDetail";
import AppGuide from "@pages/privacyPolicy/AppGuide";
import OrderFail from "@pages/order/OrderFail";
import { Event } from "@pages/event";
import ReviewSuccess from "@pages/review/ReviewSucces";
// import FilterPage from "@pages/search/FilterPage";

const RouterContainer = styled.div({
  position: "relative",
  width: "100dvw",
  maxWidth: "560px",
  // height: "100dvh",
  minHeight: "100dvh",
  display: "flex",
  flexDirection: "column",
});

const RoutesWrapper = styled.div<{ haveFooter: boolean }>(({ haveFooter }) => ({
  flex: 1,
  overflow: "auto",
  paddingBottom: haveFooter ? "8rem" : "0",

  "&::-webkit-scrollbar": {
    display: "none",
  },
}));

const shouldRenderHeader = (pathName: string) => {
  const headerRoutes = [
    "/my-page",
    "/saved",
    "/seller",
    "/brand",
    "/main-register",
  ];
  return (
    pathName === "/" || headerRoutes.some((route) => pathName.startsWith(route))
  );
};

const shouldRenderFooter = (pathName: string) => {
  const footerRoutes = [
    "/my-page",
    "/saved",
    "/seller",
    "/brand",
    "/search",
    "/main-register",
  ];

  if (pathName === "/search-results") {
    return null;
  } else if (pathName.includes("/product-detail")) {
    return "purchase";
  } else if (
    pathName === "/" ||
    footerRoutes.some((route) => pathName.startsWith(route))
  ) {
    return "default";
  }
  return null;
};
const Router = () => {
  const location = useLocation();
  const pathName = location.pathname;
  const routesWrapperRef = useRef<HTMLDivElement | null>(null);
  const { isAuthenticated } = useAuth();
  const { userInfo, setUserInfo } = useUserInfoStore();
  const footerType = shouldRenderFooter(pathName);

  const { getUserInfo } = useApiRequest();

  const {
    data: userInfoData,
    isLoading: userLoading,
    isSuccess: userInfoSuccess,
  } = useQuery({
    queryKey: ["userInfo"],
    queryFn: getUserInfo,
    enabled: isAuthenticated,
  });

  useEffect(() => {
    setUserInfo(userInfoData);
  }, [userInfoData, userInfoSuccess, setUserInfo]);

  useEffect(() => {
    if (routesWrapperRef.current) {
      routesWrapperRef.current.scrollTo(0, 0);
    }
  }, [location.pathname]);

  return (
    <RouterContainer>
      {shouldRenderHeader(pathName) && <Header />}

      <RoutesWrapper haveFooter={!!footerType} ref={routesWrapperRef}>
        <Routes>
          {
            <>
              <Route element={<Public />}>
                <Route path="/" element={<Home />} />

                {/* 이메일 인증 페이지 */}
                <Route path="/verify-email" element={<VerifyEmail />} />

                {/* 회원가입 페이지 */}
                <Route path="/join" element={<JoinPage />} />

                {/* 로그인 페이지 */}
                <Route path="/login" element={<LoginPage />} />

                {/* 로그인 페이지 */}
                <Route path="/please-login" element={<PleaseLogin />} />

                {/* 비밀번호찾기 페이지 */}
                <Route path="/find-password" element={<FindPasswordPage />} />

                {/*카카오 REDIRECT URL*/}
                <Route path="/kakao/oauth" element={<KakaoRedirect />} />

                {/* 앱 실행 REDIRECT URL */}
                <Route path="/app/redirect/:path" element={<AppRedirect />} />

                {/*SEARCH*/}
                <Route path="/search" element={<Search />} />

                {/*검색 결과*/}
                <Route path="/search-results" element={<SearchDetail />} />

                {/*브랜드 전체보기*/}
                <Route path="/search/brand" element={<SearchBrand />} />

                {/*셀러 전체보기*/}
                <Route path="/search/seller" element={<SearchSeller />} />

                {/*상품 전체보기*/}
                <Route path="/search/product" element={<SearchProduct />} />

                {/*카테고리 검색 상품 리스트*/}
                <Route
                  path="/search/category"
                  element={<CategorySearchDetail />}
                />

                {/*상품 상세정보*/}
                <Route
                  path="/product-detail/:productId"
                  element={<ProductDetail />}
                />

                {/*브랜드 상세 페이지*/}
                <Route path="/brand/:brandId" element={<Brand />} />
              </Route>

              <Route element={<Private />}>
                {/* 결제취소 */}
                {/* <Route path="/cancel" element={< />} /> */}

                {/*알림*/}
                <Route path="/alarm" element={<Alarm />} />
                {/*새소식 상세페이지*/}
                <Route path="/news/:newsId" element={<NewsDetail />} />
                {/*마이페이지*/}
                <Route path="/my-page" element={<MyPage />} />
                {/*판매 정책 설정*/}
                <Route path="/sales-policy" element={<SalesPolicy />} />
                {/*배송지관리*/}
                <Route
                  path="/delivery-management"
                  element={<DeliveryManagement DeliveryManagement={true} />}
                />
                {/*계좌관리*/}
                <Route
                  path="/bank-account-management"
                  element={<BankAccountManagement />}
                />
                {/* 앱 설정 */}
                <Route path="/setting" element={<SettingApp />} />
                {/* 계정관리 */}
                <Route
                  path="/account-management"
                  element={<AccountManagement />}
                />
                {/*비밀번호 변경*/}
                <Route path="/change-password" element={<ChangePassword />} />
                {/* 구독 */}
                <Route path="/subscribe" element={<Subscribe />} />
                {/* 구독 상세내역 */}
                <Route path="/subscribe-detail" element={<SubscribeDetail />} />

                {/* 구매/판매 문의 */}
                <Route path="/sell-buy-inquiry" element={<SellBuyInquiry />} />
                {/* FAQ */}
                <Route path="/faq" element={<Faq />} />
                {/* 1:1 문의하기 */}
                <Route path="/one-inquiry" element={<OneInquiry />} />
                {/* 리뷰 */}
                <Route path="/review/:sellerId" element={<Review />} />
                {/*SAVED*/}
                <Route path="/saved" element={<Saved />} />

                <Route path="/product-edit" element={<ProductEdit />} />

                {/*상품 문의 페이지*/}
                <Route
                  path="/product-qna/:productId"
                  element={<ProductQnA />}
                />

                {/*셀러 상세 페이지*/}
                <Route path="/seller/:sellerId" element={<Seller />} />
                {/*팔로우 상세 페이지*/}
                <Route path="/follow/:userId" element={<Follow />} />
                {/*프로필 편집 페이지*/}
                <Route path="/profile-edit" element={<ProfileEditPage />} />

                {/*상품등록 페이지*/}
                <Route path="/register" element={<RegisterProduct />} />
                <Route path="/main-register" element={<MainRegister />} />
                <Route path="/product-popup" element={<ProductPopup />} />
                {/*상품등록 성공 페이지*/}
                <Route path="/success-register" element={<SuccessRegister />} />

                {/* 장바구니 페이지 */}
                <Route path="/cart" element={<Cart />} />

                {/* 결제 페이지 */}
                <Route path="/order" element={<Order />} />
                <Route path="/success" element={<OrderSuccess />} />
                <Route path="/fail" element={<OrderFail />} />
                <Route path="/order-list" element={<OrderList />} />
                <Route
                  path="/order-detail/:orderId"
                  element={<OrderDetail />}
                />

                {/* 판매 내역 페이지 */}
                <Route path="/sales-list" element={<SalesList />} />

                {/* 판매 내역 상세 페이지 */}
                <Route
                  path="/sales-detail/:salesId"
                  element={<SalesDetail />}
                />

                <Route
                  path="/add-review/:orderId/:orderSellerId"
                  element={<AddReview />}
                />

                <Route path="/review-success" element={<ReviewSuccess />} />

                {/* 약관 페이지 */}
                <Route path="/privacy" element={<PrivacyPolicy />} />

                {/* 찾기 페이지 */}
                <Route path="/terms" element={<Terms />} />

                {/* 앱 서비스 이용가이드 */}
                <Route path="/app-guide" element={<AppGuide />} />

                {/* 구독 페이지 */}
                <Route
                  path="/subscribe-success"
                  element={<SubscribeSuccess />}
                />
                <Route path="/subscribe-fail" element={<SubscribeFail />} />

                <Route path="/event/:promotionId" element={<Event />} />
              </Route>
            </>
          }
        </Routes>
      </RoutesWrapper>

      {footerType === "purchase" && isAuthenticated && <PurchaseFooter />}
      {footerType === "purchase" && !isAuthenticated && <Footer />}
      {footerType === "default" && <Footer />}
    </RouterContainer>
  );
};

export default Router;
